import axios from 'axios';
import { trackEvent } from '../utils/analytics';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || '/api';

const api = axios.create({
    baseURL: API_BASE_URL,

    timeout: 30000,
    // The claim session is an HttpOnly cookie set by the api host, which is a
    // different origin from the site (steamplayti.me → api.steamplayti.me).
    // Without this axios drops it and every claim call reads as signed-out.
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
});

// A refused lookup is the one thing the product deliberately does NOT record:
// a profile whose library we cannot read is never inserted, so there is no row,
// no sync and no trace of the attempt anywhere. GA is therefore the only place
// this can be counted. Reported from the interceptor rather than the two call
// sites so it fires exactly once per refusal, whichever screen asked.
api.interceptors.response.use(
    response => response,
    error => {
        const code = error?.response?.data?.code;

        if (code) {
            trackEvent('registration_rejected', { reason: code });
        }

        return Promise.reject(error);
    }
);

export const playtimeAPI = {

    getPlaytimeDiffBySteamId: (steamId, params = undefined) => api.get(`/playtime/diff/${steamId}`, params ? { params } : undefined),

    // `login: false` for a visit that is not the owner's (see Dashboard's
    // isBrowseVisit) — the api then leaves last_login_at alone.
    getUserProfile: (steamId, { login = true } = {}) => (login
        ? api.get(`/users/${steamId}`)
        : api.get(`/users/${steamId}`, { params: { login: 0 } })),

    getUserTopGames: steamId => api.get(`/users/${steamId}/top-games`),

    // Claimed profiles only: 403 on an unclaimed one (no `code`, so the
    // interceptor above does not count it as a refused registration).
    getAchievementHistory: (steamId, params) => api.get(`/users/${steamId}/achievements`, { params }),

    getSyncStatus: steamId => api.get(`/sync-status/${steamId}`),

    getYesterdayPlaytime: () => api.get('/stats/yesterday-playtime'),

    // Anonymous site-wide figures for the /page/* SEO pages ({date, insights}).
    getSiteInsights: () => api.get('/stats/insights'),

    // Warms the social share card ahead of a post. Best-effort: callers should
    // not block the compose window on it (see ShareCard.vue).
    prepareShareCard: steamId => api.post(`/share/prepare/${steamId}`),
};

export const claimAPI = {
    // The viewed profile is passed along so the answer covers both facts the UI
    // needs: who is signed in, and whether *this* profile is already claimed.
    getStatus: steamId => api.get('/claim/status', steamId ? { params: { steamId } } : undefined),

    submitEmail: email => api.post('/claim/email', { email }),

    // Full page navigation, not XHR: Steam's OpenID is a redirect flow, so the
    // browser has to leave the SPA and come back. The profile being viewed goes
    // along so Steam returns the user to the page they started on — that is what
    // lets the client notice "you signed in as someone else".
    // `lookupKey` is the raw string the visitor typed. It is carried so the
    // login can be stamped onto the attempt row that string opened: a vanity
    // name is not a SteamID64, so without it the round-trip loses the link and
    // one visitor ends up as two rows — one of which can never be marked
    // claimed. It is analytics only; the landing page still comes from
    // `profile`.
    startSteamLogin: (profileSteamId, lookupKey = '') => {
        const parts = [];

        if (profileSteamId) {
            parts.push(`profile=${encodeURIComponent(profileSteamId)}`);
        }

        if (lookupKey && lookupKey !== profileSteamId) {
            parts.push(`lookup=${encodeURIComponent(lookupKey)}`);
        }

        const query = parts.length ? `?${parts.join('&')}` : '';

        window.location.href = `${API_BASE_URL}/auth/steam${query}`;
    },
};

// The site login: a 30-day HttpOnly cookie opened by any Steam return (the
// claim flow included), read by the settings page.
export const accountAPI = {
    get: () => api.get('/account'),

    saveSettings: settings => api.post('/account/settings', settings),

    // Full navigation into Steam OpenID; the api sends the browser back to
    // /settings afterwards.
    startLogin: () => {
        window.location.href = `${API_BASE_URL}/auth/steam?next=settings`;
    },

    // The api also sets a non-HttpOnly `spt_li` marker beside the login, so the
    // nav can pick "Settings" over "Sign in" without an /account request.
    probablySignedIn: () => typeof document !== 'undefined' && /(?:^|;\s*)spt_li=1(?:;|$)/.test(document.cookie),
};

export default api;
